import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { useEffect, useState } from "react";
import { prs } from "@/lib/api";
import { flagClass, actionClass, priceLabel, vendorLabel, fmtAmt, fmtDate } from "@/lib/flags";
import Sparkline from "@/components/Sparkline";
import ItemInsightPopover from "@/components/ItemInsightPopover";
import { toast } from "sonner";
import { Loader2, CheckCircle2, XCircle, PauseCircle, Clock, Stamp, BadgeCheck } from "lucide-react";

const statusBadge = (label) => {
  if (label?.startsWith("Approved")) return { cls: "bg-emerald-100 text-emerald-800 border-emerald-200", Icon: CheckCircle2 };
  if (label?.startsWith("Rejected")) return { cls: "bg-rose-100 text-rose-800 border-rose-200", Icon: XCircle };
  if (label?.startsWith("On Hold") || label === "Hold") return { cls: "bg-yellow-100 text-yellow-900 border-yellow-300", Icon: PauseCircle };
  if (label?.includes("Authorised")) return { cls: "bg-indigo-100 text-indigo-800 border-indigo-200", Icon: BadgeCheck };
  if (label?.includes("Pending Authoriser")) return { cls: "bg-slate-100 text-slate-700 border-slate-300", Icon: Clock };
  return { cls: "bg-slate-100 text-slate-700 border-slate-300", Icon: Clock };
};

export default function MyPRDetailModal({ vocNo, open, onClose, statusLabel }) {
  const [pr, setPR] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !vocNo) return;
    setPR(null);
    setLoading(true);
    prs.get(vocNo)
      .then((r) => setPR(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load PR"))
      .finally(() => setLoading(false));
  }, [vocNo, open]);

  const { cls: badgeCls, Icon: BadgeIcon } = statusBadge(statusLabel);

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="sm:max-w-2xl w-full overflow-y-auto" data-testid="mypr-detail-modal">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2 text-base">
            <span className="font-mono text-cyan-800">PR #{vocNo}</span>
            {statusLabel && (
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${badgeCls}`}>
                <BadgeIcon size={11} /> {statusLabel}
              </span>
            )}
          </SheetTitle>
          <SheetDescription>Read-only view of your submitted PR with AI scoring and decision trail.</SheetDescription>
        </SheetHeader>

        {loading && <div className="py-12 text-center text-slate-500"><Loader2 className="animate-spin inline mr-2" size={16} /> Loading…</div>}

        {pr && (
          <div className="mt-4 space-y-5">
            {/* header */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
              <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Date</div><div className="font-medium">{fmtDate(pr.vdate)}</div></div>
              <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Type</div><div className="font-medium">{pr.purc_type}</div></div>
              <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Amount</div><div className="font-bold num">₹{fmtAmt(pr.amount)}</div></div>
              <div data-testid="mypr-created-by">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Created by</div>
                <div className="font-medium">{pr.creator?.full_name?.replace(/\s*\(\d+\)\s*$/, "") || <span className="text-slate-400">—</span>}</div>
                {pr.creator?.emp_no && <div className="text-[10px] text-slate-500 mono">#{pr.creator.emp_no}{pr.creator.created_at ? ` · ${fmtDate(pr.creator.created_at)}` : ""}</div>}
              </div>
              <div data-testid="mypr-requestor" className="sm:col-span-2">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Requestor</div>
                <div className="font-medium">{pr.requestor_name || pr.requestor || <span className="text-slate-400">—</span>}</div>
                {pr.requestor && <div className="text-[10px] text-slate-500 mono">#{pr.requestor}</div>}
              </div>
              <div className="col-span-2 sm:col-span-3"><div className="text-[10px] uppercase tracking-wider text-slate-500">Supplier</div><div className="font-medium">{pr.supplier} <span className="mono text-xs text-slate-500">({pr.pcode})</span></div></div>
            </div>

            {/* decision trail */}
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Decision trail</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div className="flex items-start gap-2">
                  <Stamp size={14} className="mt-0.5 text-slate-500" />
                  <div>
                    <div className="text-[11px] text-slate-500">Authorise (L1)</div>
                    <div className="font-medium">{pr.auth_by ? `${pr.auth_by}` : <span className="text-slate-400">Pending</span>}</div>
                    {pr.auth_date && <div className="text-[10px] text-slate-500">{fmtDate(pr.auth_date)}</div>}
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <BadgeCheck size={14} className="mt-0.5 text-slate-500" />
                  <div>
                    <div className="text-[11px] text-slate-500">Approve (L2)</div>
                    <div className="font-medium">{pr.app_by ? `${pr.app_by}` : <span className="text-slate-400">Pending</span>}</div>
                    {pr.app_date && <div className="text-[10px] text-slate-500">{fmtDate(pr.app_date)}</div>}
                  </div>
                </div>
              </div>
            </div>

            {/* overall AI score */}
            {pr.risk_score !== undefined && (
              <div className="border border-slate-200 rounded-lg p-3 flex items-center justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">AI Risk Score</div>
                  <div className="text-2xl font-extrabold num">{pr.risk_score}</div>
                </div>
                <span className={`inline-flex px-3 py-1 rounded-full text-xs font-bold border ${actionClass(pr.ai_action)}`}>
                  {pr.ai_action}
                </span>
              </div>
            )}

            {/* items */}
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Line items ({pr.items?.length || 0})</div>
              <div className="space-y-2">
                {(pr.items || []).map((it, i) => (
                  <div key={`${it.icode || "noicode"}-${i}`} className="border border-slate-200 rounded-lg p-3" data-testid={`mypr-item-${i}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-medium text-sm flex items-center gap-1.5">
                          <span className="truncate">{it.iname}</span>
                          <ItemInsightPopover item={it} supplierName={pr.supplier} />
                        </div>
                        <div className="text-[10px] text-slate-500 mono">{it.icode}</div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="text-xs text-slate-500">
                          {it.qty} × {pr.currency && pr.currency !== "INR" ? `${pr.currency} ${fmtAmt(it.rate)}` : `₹${fmtAmt(it.rate)}`}
                        </div>
                        <div className="font-semibold num" title={pr.currency && pr.currency !== "INR" ? `Converted at ExchRate ${fmtAmt(pr.exch_rate)}` : ""}>
                          ₹{fmtAmt(it.amt != null ? it.amt : (it.qty || 0) * (it.rate || 0) * (pr.exch_rate || 1))}
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {it.price_flag && (
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold border ${flagClass(it.price_flag.flag)}`}>
                          Price · {priceLabel(it.price_flag)}
                        </span>
                      )}
                      {it.vendor_flag && (
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold border ${flagClass(it.vendor_flag.flag)}`}>
                          Vendor · {vendorLabel(it.vendor_flag)}
                        </span>
                      )}
                      {it.vendor_flag?.lowest_vendor && it.vendor_flag.flag !== "green" && (
                        <span className="text-[10px] text-slate-600 self-center">→ {it.vendor_flag.lowest_vendor} @ ₹{fmtAmt(it.vendor_flag.lowest_rate)}</span>
                      )}
                    </div>
                    {it.price_history?.length > 1 && (
                      <div className="mt-2"><Sparkline points={it.price_history} /></div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
